import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';

/**
 * Login page — route: /login
 *
 * Renders a username/password form and calls AuthContext.login() on submit.
 * On success, redirects to:
 *   - The originally intended page (if the user was bounced here by a protected route), or
 *   - /admin for admin users, or
 *   - /dashboard for regular users.
 *
 * State:
 *   formData     — controlled inputs for username and password
 *   error        — error message shown when login fails
 *   loading      — disables the submit button while the API call is in flight
 *   showPassword — toggles the password field between text and password type
 */
export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    // Controlled form values for the username and password fields.
    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });

    // Holds an error string to display under the form on failed login.
    const [error, setError] = useState('');

    // True while the login API call is pending; prevents duplicate submissions.
    const [loading, setLoading] = useState(false);

    // Controls whether the password input renders as plain text or masked.
    const [showPassword, setShowPassword] = useState(false);

    /**
     * handleSubmit — submits the login form.
     *
     * Calls AuthContext.login() with the entered credentials. On success,
     * reads location.state.from to restore the page the user was trying to
     * reach before being redirected to login. Falls back to role-based routing.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const user = await login(formData.username, formData.password);

            // Redirect to intended destination or based on role
            const from = location.state?.from?.pathname;
            if (from) {
                navigate(from, { replace: true });
            } else if (user.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen bg-background flex items-center justify-center p-4 overflow-hidden">
            {/* Background gradient blobs — mirrors the landing page hero */}
            <motion.div
                animate={{ scale: [1, 1.1, 1], rotate: [0, 5, 0] }}
                transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-300/30 to-pink-300/30 dark:from-purple-500/5 dark:to-pink-500/5 rounded-full blur-3xl pointer-events-none"
            />
            <motion.div
                animate={{ scale: [1, 1.2, 1], rotate: [0, -5, 0] }}
                transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-br from-blue-300/20 to-purple-300/20 dark:from-blue-500/5 dark:to-purple-500/5 rounded-full blur-3xl pointer-events-none"
            />
            <div className="absolute top-4 right-4 z-10">
                <ThemeToggle />
            </div>
            <Card className="w-full max-w-md relative z-10">
                <CardHeader>
                    <CardTitle>Microbial 16S Analysis</CardTitle>
                    <p className="text-sm text-muted-foreground">Sign in to your account</p>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Username</label>
                            <Input
                                type="text"
                                placeholder="Enter your username"
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Password</label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter your password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="pr-10"
                                    required
                                />
                                {/* Toggle button reveals/hides the password characters. */}
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? 'Signing in...' : 'Sign In'}
                        </Button>

                        <p className="text-sm text-center text-muted-foreground">
                            Don't have an account?{' '}
                            <Link to="/signup" className="text-primary hover:underline">
                                Sign up
                            </Link>
                        </p>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
